import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import { GroupName } from 'src/app/shared/enums/groups.enum';

import { AppConstants } from '../../../../app.constants';
import { UserCreateDto } from '../../../../modules/users/dto/user-create';
import { UserEditDto } from '../../../../modules/users/dto/user-edit';
import { UsersService } from '../../../../modules/users/users.service';
import { SnackBarService } from '../../../../shared/components/snack-bar/snack-bar.service';
import { DataTypeUtils } from '../../../../utils/data-type.util';
import { PhoneUtils } from '../../../../utils/phone-utils';
import {
	GroupsListSummary,
	IdAndMessageResponse2,
	OrgListSummary,
	PartnerRegistration,
	UserRegistration,
} from '../../models/organizations.model';
import {
	fetchCpfAvailabilityStart,
	fetchEmailAvailabilityStart,
	fetchOrgGroupsStart,
	fetchOrgsSummaryListStart,
	fetchUserRegStart,
	restoreFormReset,
} from '../../state/organizations.actions';
import {
	selectCpfAvailability,
	selectEmailAvailability,
	selectGroupsList,
	selectOrgList,
	selectRestoreForm,
	selectUser,
} from '../../state/organizations.selector';

import { OrgGroupModalComponent } from './org-group-modal/org-group-modal.component';
import { OrgListModalComponent } from './org-list-modal/org-list-modal.component';

@Component({
	selector: 'app-reset-pass-alert-dialog',
	template: `
		<app-dialog
			modalTitle="Atenção"
			titleButton="Sim"
			secTitleButton="Não"
			[secButton]="true"
			[asAlert]="true"
			(dialogClose)="dialogRef.close()"
			(dialogSave)="dialogRef.close(true)"
		>
			<p class="lh-md">
				A senha desse usuário será resetada e o processo de recuperação enviado
				por e-mail. Tem certeza que deseja continuar?
			</p>
		</app-dialog>
	`,
})
export class ResetPassAlertDialogComponent {
	constructor(public dialogRef: MatDialogRef<ResetPassAlertDialogComponent>) {}
}

@Component({
	selector: 'app-user-registration',
	styleUrls: ['./user-registration.component.scss'],
	templateUrl: './user-registration.component.html',
})
export class UserRegistrationComponent implements OnInit, OnDestroy {
	alertRef!: MatDialogRef<ResetPassAlertDialogComponent>;

	configurationForm: UntypedFormGroup;

	connectedGroupIds: string[] = [];
	connectedOrganizationsIds: string[] = [];
	cpfMask: string = DataTypeUtils.cpf.mask;
	dialogRef!: MatDialogRef<OrgGroupModalComponent | OrgListModalComponent>;
	groups: GroupsListSummary[] = [];
	groupsIds: string[] = [];
	groupsIdsToDisconnect: string[] = [];
	groupsInput: string = '';

	initialGroupsIds: string[] = [];
	initialOrganizationsIds: string[] = [];
	initialOrganizationsInput: string = '';

	inputId: string = String(Math.random());
	isDisabledOrganizationModal = true;
	isDisabledSaveButtonAction = false;

	isEnabledResetPassword = true;

	isLoading: boolean = true;
	organizationId: string = '';
	organizations: OrgListSummary[] = [];
	organizationsIds: string[] = [];

	organizationsIdsToDisconnect: string[] = [];
	organizationsInput: string = '';
	partners: PartnerRegistration[] = [];
	phoneMask = (value: string): string => {
		this.registrationForm.get('phone')?.updateValueAndValidity();

		return value ? PhoneUtils.phoneMask(value) : '';
	};
	registrationForm: UntypedFormGroup;
	userId: string = '';
	userType: string = '';
	private onPasswordResetSubscription$: Subscription = new Subscription();

	private onSubmitSubscription$: Subscription = new Subscription();
	private readonly sanusId: string = AppConstants.organization.id;
	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly activatedRoute: ActivatedRoute,
		private readonly router: Router,
		private readonly dialog: MatDialog,
		private readonly store: Store,
		private readonly usersService: UsersService,
		private readonly snackbarService: SnackBarService,
	) {
		this.registrationForm = this.fb.group({
			cpf: [
				null,
				[Validators.required, Validators.pattern(DataTypeUtils.cpf.pattern)],
			],
			email: [null, [Validators.required, Validators.email]],
			name: [null, [Validators.required]],
			phone: [
				null,
				[Validators.required, Validators.pattern(DataTypeUtils.phone.pattern)],
			],
			sanus_partner: [false],
		});
		this.configurationForm = this.fb.group({
			active: [true, [Validators.required]],
		});
	}

	get isSanusOrganization(): boolean {
		return this.organizationId === this.sanusId;
	}

	changeSanusPartner(isPartner: boolean): void {
		this.registrationForm.get('sanus_partner')?.patchValue(isPartner);
		if (isPartner) {
			this.removeUserGroups();
		}
	}

	createUser(request: UserCreateDto): void {
		this.onSubmitSubscription$ = this.usersService
			.createUser({
				dto: request,
				organizationId: this.organizationId,
			})
			.subscribe(
				async (response: IdAndMessageResponse2) => {
					this.isLoading = false;
					this.snackbarService.showSnack(response?.message, response?.type);
				},
				(error) => {
					this.isLoading = false;
					this.snackbarService.showSnack(error.error?.message, 'error');
				},
			);
	}

	editUser(request: UserEditDto): void {
		this.onSubmitSubscription$ = this.usersService
			.editUser({
				dto: request,
				organizationId: this.organizationId,
				userId: this.userId!,
			})
			.subscribe(
				async (response: IdAndMessageResponse2) => {
					this.isLoading = false;
					this.snackbarService.showSnack(response?.message, response?.type);
				},
				(error) => {
					this.isLoading = false;
					this.snackbarService.showSnack(error.error?.message, 'error');
				},
			);
	}

	isDisabledSaveButton(): boolean {
		return this.userType === 'inativo' || !this.groupsIds.length;
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.store.dispatch(restoreFormReset());
		this.onSubmitSubscription$.unsubscribe();
		this.onPasswordResetSubscription$.unsubscribe();
	}

	ngOnInit(): void {
		this.activatedRoute.params.subscribe(({ id, uId, ut }) => {
			this.organizationId = id;
			this.store.dispatch(fetchOrgsSummaryListStart({ id }));
			this.store.dispatch(fetchOrgGroupsStart({ id }));
			if (!uId) {
				this.store.select(selectGroupsList).subscribe((groupsList) => {
					this.groups = groupsList;
					this.initialGroupsIds = groupsList.map((group) => group.id);
				});
				this.store
					.select(selectOrgList)
					.subscribe((organizationsList) =>
						this.processOrganizationsList(id, organizationsList),
					);
				this.isLoading = false;
			} else {
				this.store
					.select(selectGroupsList)
					.subscribe((groupsList) => (this.groups = groupsList));
				this.store
					.select(selectOrgList)
					.subscribe(
						(organizationsList) => (this.organizations = organizationsList),
					);
				this.userId = uId;
				this.userType = ut;
				this.store.select(selectUser).subscribe((user) => {
					if (user) {
						this.isDisabledSaveButtonAction = true;
						this.registrationForm = this.fb.group({
							cpf: [
								{ disabled: true, value: user.cpf },
								[Validators.required, Validators.pattern(this.cpfMask)],
							],
							email: [user.email, [Validators.required, Validators.email]],
							name: [user.name, [Validators.required]],
							phone: [
								user.phone,
								[
									Validators.required,
									Validators.pattern(DataTypeUtils.phone.pattern),
								],
							],
							sanus_partner: [user.sanus_partner],
						});
						this.configurationForm = this.fb.group({
							active: [user.active, [Validators.required]],
						});
						const isOperator =
							user.group.filter((group) => group.name === 'operador').length >
							0;
						this.connectedGroupIds = user.group.map((group) => group.id);
						this.groupsIds = user.group.map((group) => group.id);
						this.processUserData(id, user, isOperator);
						this.isDisabledOrganizationModal = !isOperator;
						if (!user.active) {
							this.registrationForm.disable();
							this.configurationForm.disable();
							this.isEnabledResetPassword = false;
						}
						this.isLoading = false;
					}
				});
				this.store.dispatch(
					fetchUserRegStart({ id, userId: uId, userType: this.userType }),
				);
			}
		});
		this.store
			.select(selectRestoreForm)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((restore) => {
				if (restore) {
					this.updateValidation();
					this.isLoading = false;
					this.store.dispatch(restoreFormReset());
				}
			});
	}

	onCpfBlur(): void {
		if (
			this.registrationForm.get('cpf')?.value &&
			!this.registrationForm.get('cpf')!.invalid
		) {
			const cpf: string = this.registrationForm.get('cpf')!.value;
			this.store.dispatch(fetchCpfAvailabilityStart({ cpf }));
			this.store
				.select(selectCpfAvailability)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((result) => {
					if (result) {
						this.registrationForm.get('cpf')!.updateValueAndValidity();
					} else {
						this.registrationForm.get('cpf')!.setErrors({ minlength: true });
					}
				});
		}
	}

	onEmailBlur(): void {
		if (!this.registrationForm.get('email')!.invalid) {
			const email: string = this.registrationForm.get('email')!.value;
			this.store.dispatch(fetchEmailAvailabilityStart({ email }));
			this.store
				.select(selectEmailAvailability)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((result) => {
					if (result) {
						this.registrationForm.get('email')!.updateValueAndValidity();
					} else {
						this.registrationForm.get('email')!.setErrors({ minlength: true });
					}
				});
		}
	}

	onPasswordReset(): void {
		this.alertRef = this.dialog.open(ResetPassAlertDialogComponent, {
			disableClose: true,
		});
		this.alertRef.afterClosed().subscribe((save: boolean) => {
			if (save) {
				this.registrationForm.get('cpf')!.disable();

				const cpf = this.registrationForm.get('cpf')!.value;

				this.onPasswordResetSubscription$ = this.usersService
					.resetPassword(cpf)
					.subscribe(
						async () => {
							this.isLoading = false;
							this.isEnabledResetPassword = false;
						},
						(error) => {
							this.registrationForm.get('cpf')!.enable();

							this.snackbarService.showSnack(error.error?.message, 'error');

							this.isLoading = false;
						},
					);
			}
		});
	}

	onSubmit(): void {
		const request = this.processForm();

		if (request) {
			this.isLoading = true;
			if (this.userId) {
				this.editUser(request);
			} else {
				request.status = null;
				this.createUser(request);
			}
		}
	}

	openOrganizationGroupsModal(): void {
		if (this.userType !== 'inativo') {
			const isPartner = this.registrationForm.get('sanus_partner')?.value;
			this.dialogRef = this.dialog.open(OrgGroupModalComponent, {
				data: {
					groups: this.groups,
					isPartner,
					isSanus: this.isSanusOrganization,
					selected: this.groupsIds,
				},
				maxWidth: undefined,
			});
			this.dialogRef.afterClosed().subscribe((result: GroupsListSummary[]) => {
				if (!(!result || result === undefined)) {
					const ids = result.map(
						(organization: GroupsListSummary) => organization.id,
					);
					const idsDis = this.connectedGroupIds.filter(
						(ig) => !ids.includes(ig),
					);
					const names = result.map((organization: GroupsListSummary) =>
						this.processLabelCase(organization.name),
					);

					if (!names.includes('Operador')) {
						this.organizationsIdsToDisconnect =
							this.connectedOrganizationsIds.filter(
								(id) => id !== this.initialOrganizationsIds[0],
							);
						this.organizationsIds = this.initialOrganizationsIds;
						this.organizationsInput = this.initialOrganizationsInput;
						this.isDisabledOrganizationModal = true;
					} else {
						this.isDisabledOrganizationModal = false;
					}
					this.groupsIds = ids;
					this.groupsIdsToDisconnect = idsDis;
					this.groupsInput = names.toString().replace(',', ', ');
					this.isDisabledSaveButtonAction = false;
				}
			});
		}
	}

	openOrganizationListModal(): void {
		if (!(this.userType === 'inativo' || this.isDisabledOrganizationModal)) {
			this.dialogRef = this.dialog.open(OrgListModalComponent, {
				data: {
					disabled: this.organizationId,
					organizations: this.organizations,
					selected: this.organizationsIds,
				},
				maxWidth: undefined,
			});
			this.dialogRef.afterClosed().subscribe((result: OrgListSummary[]) => {
				if (!(!result || result === undefined)) {
					const ids = result.map(
						(organization: OrgListSummary) => organization.id,
					);
					const idsDis = this.connectedOrganizationsIds.filter(
						(ig) => !ids.includes(ig),
					);
					const names = result.map(
						(organization: OrgListSummary) => organization.name,
					);
					this.organizationsIds = ids;
					this.organizationsIdsToDisconnect = idsDis;
					this.organizationsInput = names.toString().replace(',', ', ');
					this.isDisabledSaveButtonAction = false;
				}
			});
		}
	}

	processForm(): any {
		this.registrationForm.get('cpf')!.enable();
		this.registrationForm.get('email')!.enable();

		const registration = this.registrationForm.value;
		const configuration = this.configurationForm.value;

		const request: any = {
			status: 4,
			...registration,
			...configuration,
			groups_id: this.groupsIds,
			orgs_id: this.organizationsIds,
		};

		if (this.userId) {
			request.orgs_disconnect = this.organizationsIdsToDisconnect;
			request.groups_disconnect = this.groupsIdsToDisconnect;
		}

		this.registrationForm.get('cpf')!.disable();

		return request;
	}

	processLabelCase(label: string) {
		if (label) {
			return label.replace(/\w\S*/g, (txt: string) => {
				return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
			});
		} else {
			return '';
		}
	}

	processOrganizationsList(
		id: string,
		organizationsList: OrgListSummary[],
	): void {
		this.organizations = organizationsList;
		for (const organization of organizationsList) {
			if (organization.id === id) {
				this.organizationsIds = [organization.id];
				this.organizationsInput = organization.name;
				this.initialOrganizationsIds = [organization.id];
				this.initialOrganizationsInput = organization.name;
			} else {
				for (const organizations of organization.secondary) {
					if (organizations.id === id) {
						this.organizationsIds = [organizations.id];
						this.organizationsInput = organizations.name;
						this.initialOrganizationsIds = [organizations.id];
						this.initialOrganizationsInput = organizations.name;

						// eslint-disable-next-line typescript-extended-rules/no-empty-return
						return;
					}
				}
			}
		}
	}

	processUserData(
		id: string,
		user: UserRegistration,
		isOperator: boolean,
	): void {
		if (user) {
			if (isOperator) {
				this.connectedOrganizationsIds = user.organization.map((o) => o.id);
				this.groupsInput = user.group
					.map((group) => this.processLabelCase(group.name))
					.toString()
					.replace(',', ', ');
				this.initialOrganizationsIds = [id];
				this.organizationsIds = user.organization.map((o) => o.id);
				let organizationLabel = '';

				for (const organization of this.organizations) {
					if (organization.id === id) {
						organizationLabel = organization.name;
						break;
					}

					for (const subsidiary of organization.secondary) {
						if (subsidiary.id === id) {
							organizationLabel = subsidiary.name;
							break;
						}
					}
				}
				this.initialOrganizationsInput = organizationLabel;
				this.organizationsInput = user.organization
					.map((o) => o.name)
					.toString()
					.replace(',', ', ');
			} else {
				this.connectedOrganizationsIds = [id];
				this.groupsInput = user.group
					.map((group) => this.processLabelCase(group.name))
					.toString()
					.replace(',', ', ');
				this.initialOrganizationsIds = [id];
				this.organizationsIds = this.initialOrganizationsIds;
				let organizationLabel = '';

				for (const organization of this.organizations) {
					if (organization.id === id) {
						organizationLabel = organization.name;
						break;
					}

					for (const subsidiary of organization.secondary) {
						if (subsidiary.id === id) {
							organizationLabel = subsidiary.name;
							break;
						}
					}
				}
				this.initialOrganizationsInput = organizationLabel;
				this.organizationsInput = this.initialOrganizationsInput;
			}
		}
	}

	/**
	 * Remove user groups (keeps: OPERADOR, BENEFICIARIO)
	 */
	removeUserGroups(): void {
		const operatorGroup = this.groups.find(
			(group) => group.name === GroupName.OPERADOR,
		);
		const beneficiaryGroup = this.groups.find(
			(group) => group.name === GroupName.BENEFICIARIO,
		);

		if (operatorGroup && beneficiaryGroup) {
			const filteredGroups = this.groups.filter(
				(group) =>
					group.id === operatorGroup.id || group.id === beneficiaryGroup.id,
			);

			this.groupsIds = filteredGroups.map((group) => group.id);
			this.groupsInput = filteredGroups
				.map((group) => this.processLabelCase(group.name))
				.join(', ');

			this.connectedGroupIds = this.groupsIds;

			const isOperatorSelected = this.groupsIds.includes(operatorGroup.id);
			this.isDisabledOrganizationModal = !isOperatorSelected;
		}
	}

	updateValidation(): void {
		for (const control in this.registrationForm.controls) {
			this.registrationForm.get(control)!.updateValueAndValidity();
		}

		for (const control in this.configurationForm.controls) {
			this.configurationForm.get(control)!.updateValueAndValidity();
		}
	}
}
